import { ChevronRight, MapPin, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useState, lazy, Suspense } from 'react';
import { Address } from '../../types/api';
import { useTranslation } from 'react-i18next';
import { analyticsService } from '../../services';

// Lazy load the modal to prevent initialization on import
const ScheduleSetupModal = lazy(() => import('../ScheduleSetupModal'));

interface AddressAndScheduleProps {
  selectedAddress: Address | null;
  selectedTime?: string;
  selectedTimeSlots?: string[];
  selectedDate?: string;
  weeklyDay?: number;
  isGift?: boolean;
  showAddressOnly?: boolean;
  showScheduleOnly?: boolean;
  onClick?: () => void;
  onScheduleUpdate?: (scheduleData: any) => void;
  // Special prop to optimize modal initialization
  __scheduleModalOptimized?: boolean;
  // Optional props for Purchase Journey tracking
  trackingData?: {
    subscriptionLevel: 'standard' | 'premium';
    subscriptionType: 'monthly' | 'yearly' | 'trial';
    serviceType: 'flower' | 'fruit' | 'cat_grass';
    currency: string;
    price: number;
  };
}

export const AddressAndScheduleSection = ({
  selectedAddress,
  selectedTimeSlots = [],
  selectedDate = '',
  weeklyDay = 1,
  isGift = false,
  showAddressOnly = false,
  showScheduleOnly = false,
  onClick,
  onScheduleUpdate,
  __scheduleModalOptimized = false,
  trackingData
}: AddressAndScheduleProps) => {
  const navigate = useNavigate();
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const { t } = useTranslation();
  
  // Only initialize modal state if we'll actually use it
  const shouldShowModal = !__scheduleModalOptimized && showScheduleModal;
  
  // Format the display time
  const formatTimeSlot = (timeSlot: string): string => {
    if (!timeSlot) return t('subscription.addressAndSchedule.timeSlot.morning');
    if (timeSlot.includes('9:00 AM')) return t('subscription.addressAndSchedule.timeSlot.morning');
    if (timeSlot.includes('2:00 PM')) return t('subscription.addressAndSchedule.timeSlot.afternoon');
    return timeSlot;
  };
  
  const displayTime = selectedTimeSlots.length > 0 
    ? selectedTimeSlots
        .filter((slot: string | null): slot is string => typeof slot === 'string')
        .map(formatTimeSlot)
        .filter((value: string, index: number, self: string[]) => self.indexOf(value) === index)
        .join(', ')
    : '';
  
  // Get day name based on weeklyDay number (0 = Sunday, 1 = Monday, etc.)
  const getDayName = (day: number | undefined) => {
    if (day === undefined) return t('subscription.addressAndSchedule.days.monday');
    const dayKeys = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    return t(`subscription.addressAndSchedule.days.${dayKeys[day]}`);
  };
  
  const dayName = getDayName(weeklyDay);

  // Helper function to format address display
  const formatAddressDisplay = () => {
    if (typeof selectedAddress === 'object' && selectedAddress !== null) {
      // Prioritize locationName and specific street details
      const addr = selectedAddress as any;
      
      // First try to extract important parts
      const locationName = addr.locationName || '';
      
      // Extract Soi information from details if available
      let soiInfo = '';
      if (addr.details && typeof addr.details === 'string') {
        const detailsText = addr.details;
        const soiMatch = detailsText.match(/Soi\s+\w+/i);
        if (soiMatch) {
          soiInfo = soiMatch[0];
        }
      }
      
      // If we have locationName or soi info, display them prominently
      if (locationName || soiInfo) {
        const mainParts = [locationName, soiInfo].filter(Boolean);
        
        // Add other address parts for context
        const secondaryParts: string[] = [];
        if (addr.street) secondaryParts.push(addr.street);
        if (addr.city) secondaryParts.push(addr.city);
        
        return mainParts.join(', ') + (secondaryParts.length > 0 ? ' • ' + secondaryParts.join(', ') : '');
      }
      
      // Fall back to original formatting logic if no special parts found
      if ('address' in selectedAddress && typeof selectedAddress.address === 'string') {
        return selectedAddress.address;
      } else if ('fullAddress' in selectedAddress && typeof selectedAddress.fullAddress === 'string') {
        return selectedAddress.fullAddress;
      } else if ('street' in selectedAddress) {
        const parts: string[] = [];
        if (addr.street) parts.push(addr.street);
        if (addr.city) parts.push(addr.city);
        if (addr.state) parts.push(addr.state);
        if (addr.zipCode) parts.push(addr.zipCode);
        return parts.join(', ');
      } else {
        // If we can't find standard address fields, show all non-id fields
        const addressParts = Object.entries(selectedAddress)
          .filter(([key, value]) => 
            !key.toLowerCase().includes('id') && 
            typeof value === 'string' && 
            value.length > 0
          )
          .map(([_, value]) => value as string);
        
        return addressParts.length > 0 
          ? addressParts.join(', ') 
          : JSON.stringify(selectedAddress);
      }
    } else if (typeof selectedAddress === 'string') {
      return selectedAddress;
    } else {
      return t('subscription.addressAndSchedule.addressSelectedUnknownFormat');
    }
  };

  const handleScheduleModalOpen = () => {
    setShowScheduleModal(true);
  };

  const handleScheduleModalClose = () => {
    setShowScheduleModal(false);
  };

  const handleScheduleSave = (scheduleData: any) => {
    if (onScheduleUpdate) {
      onScheduleUpdate(scheduleData);
    }
    setShowScheduleModal(false);
  };

  // Track shipping info when address is selected and tracking data is available
  const handleAddressClick = () => {
    // Track add_shipping_info event if we have both address and tracking data
    if (selectedAddress && trackingData) {
      try {
        const items = [{
          item_id: `${trackingData.serviceType}_${trackingData.subscriptionLevel}_${trackingData.subscriptionType}`,
          item_name: `${trackingData.serviceType === 'flower' ? 'Flower' : trackingData.serviceType === 'cat_grass' ? 'Cat Grass' : 'Fruit'} ${trackingData.subscriptionLevel} ${trackingData.subscriptionType} Subscription`,
          item_category: 'Subscription',
          item_brand: 'Piika',
          price: trackingData.price,
          quantity: 1
        }];

        // Use address city/state as shipping tier
        const shippingTier = selectedAddress.city || selectedAddress.state || 'standard';
        analyticsService.trackAddShippingInfo(shippingTier, items, trackingData.currency, trackingData.price);
        console.log('📊 Tracked add_shipping_info for address selection:', { shippingTier, items });
      } catch (analyticsError) {
        console.error('Failed to track add shipping info:', analyticsError);
      }
    }

    // Execute the original onClick or navigate
    if (onClick) {
      onClick();
    } else {
      navigate('/address', { 
        state: { 
          returnTo: '/subscription',
          selectedAddress,
          selectedTimeSlots,
          selectedDate,
          weeklyDay,
          giftType: isGift ? 'direct' : undefined
        } 
      });
    }
  };

  // Add this debug logging
  console.log('[AddressAndScheduleSection] Rendering with selectedAddress:', selectedAddress);
  console.log('[AddressAndScheduleSection] Address data:', selectedAddress ? {
    id: selectedAddress.id,
    street: selectedAddress.street,
    city: selectedAddress.city
  } : 'No address selected');
  console.log('[AddressAndScheduleSection] Props:', { showAddressOnly, showScheduleOnly });

  return (
    <div className="w-full">
      {/* Address Section - Only render if not showScheduleOnly */}
      {!showScheduleOnly && (
        <div 
          className="relative px-5 py-4 border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer"
          onClick={handleAddressClick}
        >
          <div className="flex items-start">
            <MapPin size={20} className="text-gray-400 mt-1" />
            <div className="ml-3 flex-1">
              <div className="flex justify-between items-center">
                <span className="text-base font-medium text-gray-900">{t('subscription.addressAndSchedule.deliveryAddress')}</span>
                <ChevronRight size={20} className="text-gray-400" />
              </div>
              {selectedAddress && selectedAddress.id ? (
                <div className="mt-1">
                  <p className="font-medium">
                    {selectedAddress.locationName || selectedAddress.name || selectedAddress.street || t('subscription.addressAndSchedule.selectedAddress')}
                  </p>
                  <p className="text-sm text-gray-500">
                    {selectedAddress.city ? `${selectedAddress.city}, ` : ''}
                    {selectedAddress.state ? `${selectedAddress.state} ` : ''}
                    {selectedAddress.zipCode || ''}
                  </p>
                  {selectedAddress.addressLabel && (
                    <span className={`inline-block text-xs px-2 py-1 rounded mt-1 ${
                      selectedAddress.addressLabel === 'HOME' ? 'bg-green-100 text-green-800' :
                      selectedAddress.addressLabel === 'OFFICE' ? 'bg-gray-100 text-gray-800' :
                      selectedAddress.addressLabel === 'SHOP' ? 'bg-blue-100 text-blue-800' :
                      'bg-purple-100 text-purple-800'
                    }`}>
                      {selectedAddress.addressLabel}
                    </span>
                  )}
                  <p className="text-xs text-gray-400 mt-1">{t('subscription.addressAndSchedule.addressId')}: {selectedAddress.id.substring(0, 8)}...</p>
                </div>
              ) : (
                <div className="mt-1">
                  <span className="text-sm text-[#F9D348] font-medium">{t('subscription.addressAndSchedule.addDeliveryAddress')}</span>
                </div>
              )}
            </div>
          </div>
          {/* Ripple effect overlay for touch feedback */}
          <div className="absolute inset-0 bg-black opacity-0 active:opacity-5 transition-opacity pointer-events-none" />
        </div>
      )}

      {/* Schedule Section - Always show this regardless of address selection */}
      <div 
        className="relative px-5 py-4 hover:bg-gray-50 transition-colors cursor-pointer"
        onClick={handleScheduleModalOpen}
      >
        <div className="flex items-start">
          <Clock size={20} className="text-gray-400 mt-1" />
          <div className="ml-3 flex-1">
            <div className="flex justify-between items-center">
              <span className="text-base font-medium text-gray-900">{t('subscription.addressAndSchedule.deliverySchedule')}</span>
              <ChevronRight size={20} className="text-gray-400" />
            </div>
            <div className="mt-1">
              <p className="text-sm text-gray-600 leading-relaxed pr-4">
                {t('subscription.addressAndSchedule.every')} <span className="font-medium">{dayName}</span> • {displayTime}
              </p>
            </div>
          </div>
        </div>
        {/* Ripple effect overlay for touch feedback */}
        <div className="absolute inset-0 bg-black opacity-0 active:opacity-5 transition-opacity pointer-events-none" />
      </div>

      {/* Schedule Setup Modal - Only initialize and render if needed */}
      {shouldShowModal && (
        <Suspense fallback={<div className="fixed inset-0 bg-black/50 flex items-center justify-center">
          <div className="bg-white p-4 rounded-lg shadow-xl">{t('subscription.addressAndSchedule.loadingScheduleOptions')}</div>
        </div>}>
          <ScheduleSetupModal
            isOpen={showScheduleModal}
            onClose={handleScheduleModalClose}
            initialData={{
              limitedView: true,
              fromSubscription: true,
              selectedAddress: selectedAddress,
              selectedTimeSlots: selectedTimeSlots,
              selectedDate: selectedDate ? new Date(selectedDate) : undefined,
              weeklyDay: weeklyDay,
              isWeeklySubscription: true,
              returnTo: '/subscription'
            }}
            onSave={handleScheduleSave}
          />
        </Suspense>
      )}
    </div>
  );
};

export default AddressAndScheduleSection; 